import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  PromotionCodeModal,
  PromotionCodeTable,
  PromotionCodeViewModal,
} from "../../components/backend/promotioncodes";
import {
  usePromotionCode,
  usePromotionCodeDetail,
  usePromotionCodeMutations,
  usePromotionCodes,
} from "../../hooks/usePromotionCode";
import {
  CreatePromotionCodeRequest,
  PromotionCode,
  PromotionCodeList,
  UpdatePromotionCodeRequest,
} from "../../types/admin";

import AdminPage from "./AdminPage";

type ModalState = {
  open: boolean;
  mode: "create" | "edit";
  code?: PromotionCode;
};

type ViewModalState = {
  open: boolean;
  codeId: string | null;
};

const PromotionCodesPage: React.FC = () => {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState<PromotionCodeList | null>(
    null
  );
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "info",
  });
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    mode: "create",
  });
  const [viewModalState, setViewModalState] = useState<ViewModalState>({
    open: false,
    codeId: null,
  });
  const [editingCodeId, setEditingCodeId] = useState<string | null>(null);

  const { data, isLoading } = usePromotionCodes();
  const { create, update, remove } = usePromotionCodeMutations();

  // Fetch full code details when editing
  const { data: editCodeData, isLoading: isLoadingEditCode } = usePromotionCode(
    editingCodeId || ""
  );

  // Fetch full code details for view modal
  const { data: viewCodeData, isLoading: isLoadingViewCode } =
    usePromotionCodeDetail(viewModalState.codeId || "");

  const codes = useMemo(() => data?.data ?? [], [data]);

  const openCreateModal = () =>
    setModalState({ open: true, mode: "create", code: undefined });

  const openEditModal = async (code: PromotionCodeList) => {
    // Set the code ID to fetch full details
    setEditingCodeId(code.id);
    // The modal will open once data is loaded via useEffect
  };

  const openViewModal = (code: PromotionCodeList) => {
    setViewModalState({ open: true, codeId: code.id });
  };

  const closeViewModal = () => {
    setViewModalState({ open: false, codeId: null });
  };

  // Open edit modal when full code data is loaded
  useEffect(() => {
    if (editingCodeId && editCodeData?.data && !isLoadingEditCode) {
      setModalState({ open: true, mode: "edit", code: editCodeData.data });
      setEditingCodeId(null); // Reset after opening
    }
  }, [editingCodeId, editCodeData, isLoadingEditCode]);

  const closeModal = () =>
    setModalState((prev) => ({ ...prev, open: false, code: undefined }));

  const showMessage = (message: string, severity: AlertColor = "info") =>
    setSnackbar({ open: true, message, severity });

  const getErrorMessage = (error: any, fallback: string) => {
    const errors = error?.response?.data?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return errors.join(" ");
    }
    return (
      error?.response?.data?.message ||
      error?.message ||
      fallback
    );
  };

  const handleSave = async (
    payload: CreatePromotionCodeRequest | UpdatePromotionCodeRequest
  ) => {
    try {
      if (modalState.mode === "create") {
        await create.mutateAsync(payload as CreatePromotionCodeRequest);
        showMessage(t("admin.promotionCodes.codeCreated"), "success");
      } else if (modalState.code?.id) {
        await update.mutateAsync({
          id: modalState.code.id,
          data: payload as UpdatePromotionCodeRequest,
        });
        showMessage(t("admin.promotionCodes.codeUpdated"), "success");
      }
      closeModal();
    } catch (error) {
      console.error("Failed to save promotion code", error);
      showMessage(
        getErrorMessage(error, t("admin.promotionCodes.failedToSave")),
        "error"
      );
    }
  };

  const handleDelete = async (code: PromotionCodeList) => {
    setConfirmDelete(code);
  };

  const confirmDeletion = async () => {
    if (!confirmDelete) return;
    try {
      await remove.mutateAsync(confirmDelete.id);
      showMessage(t("admin.promotionCodes.codeDeleted"), "success");
      setConfirmDelete(null);
    } catch (error: any) {
      console.error("Failed to delete promotion code", error);
      showMessage(
        getErrorMessage(
          error,
          t("admin.promotionCodes.failedToDelete")
        ),
        "error"
      );
      setConfirmDelete(null);
    }
  };

  return (
    <AdminPage>
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              {t("admin.promotionCodes.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("admin.promotionCodes.subtitle")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateModal}
          >
            {t("admin.promotionCodes.addCode")}
          </Button>
        </Box>

        <PromotionCodeTable
          codes={codes}
          loading={
            isLoading ||
            create.isPending ||
            update.isPending ||
            remove.isPending
          }
          onEdit={openEditModal}
          onDelete={handleDelete}
          onView={openViewModal}
        />

        <PromotionCodeModal
          open={modalState.open}
          mode={modalState.mode}
          initialData={modalState.code}
          onClose={closeModal}
          onSave={handleSave}
          submitting={create.isPending || update.isPending}
        />

        <PromotionCodeViewModal
          open={viewModalState.open}
          codeDetail={viewCodeData?.data || null}
          loading={isLoadingViewCode}
          onClose={closeViewModal}
        />

        <Dialog
          open={Boolean(confirmDelete)}
          onClose={() => setConfirmDelete(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t("admin.promotionCodes.deleteTitle")}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              {t("admin.promotionCodes.confirmDelete", { code: confirmDelete?.code })}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {t("admin.promotionCodes.deleteWarning")}
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)}>{t("admin.promotionCodes.cancel")}</Button>
            <Button
              color="error"
              variant="contained"
              onClick={confirmDeletion}
              disabled={remove.isPending}
            >
              {t("admin.promotionCodes.delete")}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            variant="filled"
            sx={{ maxWidth: 600 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </AdminPage>
  );
};

export default PromotionCodesPage;
